import { useState, useEffect, useRef } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useSearch } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MoreHorizontal, Trash2, Flame, Snowflake, Wind, Search, Plus, Sun, CloudSnow, Thermometer, Loader2, Pencil, Check, X, Info, Copy, Building2, Play } from "lucide-react";
import { SiGoogle } from "react-icons/si";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { SeamConnection, ThermostatPreset, Property } from "@shared/schema";
import { sortProperties } from "@/lib/property-utils";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";
import { describeApplyResult, readStoredApply, type PresetApplyResponse } from "@/lib/thermostat-preset-apply";
import { ThermostatPresetUnavailableDevices, getUnavailablePresetDeviceIds } from "@/components/thermostat-preset-unavailable-devices";
import { ThermostatPresetSeasonFields } from "@/components/thermostat-preset-season-fields";
import { filterThermostatRows, thermostatRowComparator } from "@/lib/thermostat-list";

type ThermostatsTab = "connected_accounts" | "presets" | "settings";
type ConnectionSubTab = "connected_accounts" | "devices";

const MONTHLY_PRICE_PER_THERMOSTAT = 2.5;

export interface ThermostatDeviceRow {
  deviceId: string;
  seamName: string;
  customName: string | null;
  propertyId: number | null;
  propertyIds: number[];
  provider: string;
  accountEmail: string | null;
  location: string | null;
  online: boolean | null;
  issueReasons?: ("offline" | "out_of_range" | "unassigned")[];
  // Latest stored polled reading (Fahrenheit). Surfaced as a "Current Temp"
  // column so operators can scan device health at a glance.
  currentTemperatureF?: number | null;
}

export interface PresetFormState {
  name: string;
  propertyId: string;
  summerHeatSetPoint: number;
  summerCoolSetPoint: number;
  winterHeatSetPoint: number;
  winterCoolSetPoint: number;
  fanMode: string;
  deviceIds: string[];
  allowMemberControl: boolean;
  summerStartMonth: number;
  summerStartDay: number;
  summerEndMonth: number;
  summerEndDay: number;
  winterStartMonth: number;
  winterStartDay: number;
  winterEndMonth: number;
  winterEndDay: number;
}

type PresetControlSettings = Pick<PresetFormState, "allowMemberControl" | "summerStartMonth" | "summerStartDay" | "summerEndMonth" | "summerEndDay" | "winterStartMonth" | "winterStartDay" | "winterEndMonth" | "winterEndDay">;
type ThermostatPresetWithControls = ThermostatPreset & Partial<PresetControlSettings>;

interface SeamDevice {
  device_id: string;
  display_name: string;
  device_type: string;
  properties?: { online?: boolean };
  location?: { location_name?: string };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const PROVIDER_LABELS: Record<string, string> = {
  google_nest: "Google Nest",
  honeywell_resideo: "Honeywell",
  ecobee: "Ecobee",
  sensi: "Sensi",
  tado: "tado°",
  smartthings: "SmartThings",
};

function formatProviderLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function presetSeasonRange(preset: ThermostatPresetWithControls, season: "summer" | "winter"): string {
  const startMonth = preset[`${season}StartMonth` as keyof PresetControlSettings] as number | undefined;
  const startDay = preset[`${season}StartDay` as keyof PresetControlSettings] as number | undefined;
  const endMonth = preset[`${season}EndMonth` as keyof PresetControlSettings] as number | undefined;
  const endDay = preset[`${season}EndDay` as keyof PresetControlSettings] as number | undefined;
  return `${MONTHS[(startMonth ?? (season === "summer" ? 5 : 10)) - 1]} ${startDay ?? 1} – ${MONTHS[(endMonth ?? (season === "summer" ? 9 : 4)) - 1]} ${endDay ?? (season === "summer" ? 30 : 30)}`;
}

export default function AppDevicesThermostats() {
  const queryClient = useQueryClient();
  usePageTitle("Thermostats");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("devices");
  const [activeTab, setActiveTab] = useTabQueryParam("tab", "connected_accounts", {
    tabs: ["connected_accounts", "presets", "settings"],
  });
  const effectiveTab = activeTab === "settings" ? "presets" : activeTab;
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("tab") === "settings") setActiveTab("presets");
  }, [setActiveTab]);
  // intentionally local — sub-tab within the "connected_accounts" tab panel, not a top-level page tab
  const [connectionSubTab, setConnectionSubTab] = useState<ConnectionSubTab>("connected_accounts");
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [connectStep, setConnectStep] = useState<"select" | "waiting">("select");
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const pollIntervalRef = useRef<number | null>(null);
  const popupCheckRef = useRef<number | null>(null);
  const [showPresetDialog, setShowPresetDialog] = useState(false);
  const [presetStep, setPresetStep] = useState<1 | 2 | 3>(1);
  const [editingPreset, setEditingPreset] = useState<ThermostatPreset | null>(null);
  const [presetForm, setPresetForm] = useState({
    name: "",
    propertyId: "",
    summerHeatSetPoint: 68,
    summerCoolSetPoint: 73,
    winterHeatSetPoint: 68,
    winterCoolSetPoint: 73,
    fanMode: "auto",
    deviceIds: [] as string[],
    allowMemberControl: true,
    summerStartMonth: 5,
    summerStartDay: 1,
    summerEndMonth: 9,
    summerEndDay: 30,
    winterStartMonth: 10,
    winterStartDay: 1,
    winterEndMonth: 4,
    winterEndDay: 30,
  });
  const [presetSearch, setPresetSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "offline" | "online">("all");
  const [sortColumn, setSortColumn] = useState<"name" | "status" | "properties" | "provider" | "account">("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const { toast } = useToast();
  // Deep-link support: `/app/devices/thermostats?deviceId=<id>` from the
  // "Thermostat is out of range" notification should jump straight to the
  // matching device row in the Devices subtab and briefly flash it so the
  // user immediately sees which device alerted.
  const search = useSearch();
  const highlightedDeviceId = new URLSearchParams(search).get("deviceId");
  const [attentionOnly, setAttentionOnly] = useState(() => new URLSearchParams(search).get("attention") === "1");
  useEffect(() => {
    setAttentionOnly(new URLSearchParams(search).get("attention") === "1");
  }, [search]);
  function setAttentionDeepLink(enabled: boolean) {
    const params = new URLSearchParams(window.location.search);
    if (enabled) {
      params.set("subtab", "devices");
      params.set("attention", "1");
    } else {
      params.delete("attention");
    }
    window.history.replaceState(null, "", `${window.location.pathname}${params.size ? `?${params}` : ""}`);
    setAttentionOnly(enabled);
    if (enabled) setConnectionSubTab("devices");
  }
  const requestedPropertyParam = new URLSearchParams(search).get("propertyId");
  const requestedPropertyId = requestedPropertyParam ? parseInt(requestedPropertyParam, 10) : null;
  // Deep-link support for the Overview "Need to make adjustments?" callout:
  // `/app/devices/thermostats?subtab=devices` should land directly on the
  // Devices subtab so operators can immediately rename or reassign devices.
  const requestedSubtab = new URLSearchParams(search).get("subtab");
  const handledSubtabRef = useRef<string | null>(null);
  const [flashingDeviceId, setFlashingDeviceId] = useState<string | null>(null);
  const deviceRowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const handledHighlightRef = useRef<string | null>(null);
  const handledPropertyParamRef = useRef<number | null>(null);

  // Used to pass targetOwnerId to the connect-webview endpoint so connections
  // are always stored under the owner's account (not the team member's own id).
  const authMeQuery = useQuery<{ activeMembership?: { ownerUserId: number } | null }>({
    queryKey: ["/api/app/auth/me"],
    staleTime: 60 * 1000,
  });
  const activeOwnerUserId = authMeQuery.data?.activeMembership?.ownerUserId;

  const connectionsQuery = useQuery<SeamConnection[]>({
    queryKey: ["/api/app/devices/connections", "thermostat"],
    queryFn: async () => {
      const res = await fetch("/api/app/devices/connections?deviceType=thermostat", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch connections");
      return res.json();
    },
  });

  const presetsQuery = useQuery<ThermostatPreset[]>({
    queryKey: ["/api/app/devices/thermostat-presets"],
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const addPropertyMutation = useMutation({
    mutationFn: async ({ deviceId, propertyId }: { deviceId: string; propertyId: number }) => {
      const res = await apiRequest("PUT", `/api/app/devices/thermostats/${encodeURIComponent(deviceId)}/property`, { propertyId });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw Object.assign(new Error(body.message || "Failed to assign property"), { code: body.code });
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/thermostats/property-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/thermostats/property-counts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/seam/thermostats/all"] });
      toast({ title: "Property assigned" });
    },
    onError: (error: Error & { code?: string }) => {
      toast({ title: error.message || "Failed to assign property", variant: "destructive" });
    },
  });

  const removePropertyMutation = useMutation({
    mutationFn: async ({ deviceId, propertyId }: { deviceId: string; propertyId: number }) => {
      const res = await apiRequest("DELETE", `/api/app/devices/thermostats/${encodeURIComponent(deviceId)}/property/${propertyId}`, undefined);
      if (!res.ok) throw new Error("Failed to remove property");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/thermostats/property-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/thermostats/property-counts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/seam/thermostats/all"] });
      toast({ title: "Property removed" });
    },
    onError: () => {
      toast({ title: "Failed to remove property", variant: "destructive" });
    },
  });

  function handleAddProperty(deviceId: string, propertyId: string) {
    if (!propertyId || propertyId === "__unassign__") return;
    const pid = parseInt(propertyId, 10);
    addPropertyMutation.mutate({ deviceId, propertyId: pid });
  }

  function handleRemoveProperty(deviceId: string, propertyId: number) {
    removePropertyMutation.mutate({ deviceId, propertyId });
  }

  const thermostatDevicesQuery = useQuery<ThermostatDeviceRow[]>({
    queryKey: ["/api/app/devices/seam/thermostats/all"],
  });

  const [editingDeviceId, setEditingDeviceId] = useState<string | null>(null);
  const [editingDeviceName, setEditingDeviceName] = useState("");

  const renameDeviceMutation = useMutation({
    mutationFn: async (input: { deviceId: string; customName: string | null }) => {
      return apiRequest("PATCH", `/api/app/devices/seam/thermostats/${encodeURIComponent(input.deviceId)}`, {
        customName: input.customName,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/seam/thermostats/all"] });
      setEditingDeviceId(null);
      setEditingDeviceName("");
      toast({ title: "Device renamed" });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Failed to rename device", variant: "destructive" });
    },
  });

  const assignDevicePropertyMutation = useMutation({
    mutationFn: async (input: { deviceId: string; propertyId: number | null }) => {
      return apiRequest("PATCH", `/api/app/devices/seam/thermostats/${encodeURIComponent(input.deviceId)}`, {
        propertyId: input.propertyId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/seam/thermostats/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/thermostats/property-counts"] });
      toast({ title: "Property updated" });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Failed to update property", variant: "destructive" });
    },
  });


  function clearConnectTimers() {
    if (pollIntervalRef.current !== null) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (popupCheckRef.current !== null) {
      window.clearInterval(popupCheckRef.current);
      popupCheckRef.current = null;
    }
  }

  function resetConnectFlow() {
    clearConnectTimers();
    if (popupRef.current && !popupRef.current.closed) {
      try { popupRef.current.close(); } catch {}
    }
    popupRef.current = null;
    setConnectStep("select");
    setConnectingProvider(null);
    setConnectError(null);
  }

  useEffect(() => {
    return () => clearConnectTimers();
  }, []);

  // When a `propertyId` query param is present (e.g. from the Messages member
  // sidebar "Thermostat" link), switch to the Devices subtab and filter to
  // that property's devices so the host lands in the right context. Fires once
  // per propertyId value so the user can navigate freely afterwards.
  const [filteredPropertyId, setFilteredPropertyId] = useState<number | null>(null);
  useEffect(() => {
    if (requestedPropertyId == null || isNaN(requestedPropertyId)) return;
    if (handledPropertyParamRef.current === requestedPropertyId) return;
    handledPropertyParamRef.current = requestedPropertyId;
    setActiveTab("connected_accounts");
    setConnectionSubTab("devices");
    setFilteredPropertyId(requestedPropertyId);
  }, [requestedPropertyId]);

  // When a `deviceId` query param is present, switch to the Devices subtab so
  // the matching row is rendered. We do this once per param value so the user
  // can still navigate the tabs freely afterwards.
  useEffect(() => {
    if (!highlightedDeviceId) return;
    setActiveTab("connected_accounts");
    setConnectionSubTab("devices");
  }, [highlightedDeviceId]);

  useEffect(() => {
    if (!requestedSubtab) return;
    if (handledSubtabRef.current === requestedSubtab) return;
    handledSubtabRef.current = requestedSubtab;
    if (requestedSubtab === "devices") {
      setActiveTab("connected_accounts");
      setConnectionSubTab("devices");
    }
  }, [requestedSubtab]);

  // Once the devices have loaded and the row is mounted, scroll it into view
  // and flash a ring so the alerting device is immediately obvious. Falls
  // back gracefully if the deviceId is not in the list (no scroll, no flash).
  useEffect(() => {
    if (!highlightedDeviceId) return;
    if (handledHighlightRef.current === highlightedDeviceId) return;
    if (connectionSubTab !== "devices") return;
    const devices = thermostatDevicesQuery.data;
    if (!devices) return;
    if (!devices.some((d) => d.deviceId === highlightedDeviceId)) {
      handledHighlightRef.current = highlightedDeviceId;
      return;
    }
    const row = deviceRowRefs.current[highlightedDeviceId];
    if (!row) return;
    handledHighlightRef.current = highlightedDeviceId;
    row.scrollIntoView({ behavior: "smooth", block: "center" });
    setFlashingDeviceId(highlightedDeviceId);
    const timer = window.setTimeout(() => setFlashingDeviceId(null), 2500);
    return () => window.clearTimeout(timer);
  }, [highlightedDeviceId, connectionSubTab, thermostatDevicesQuery.data]);

  const connectMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (provider: string) => {
      const res = await apiRequest("POST", "/api/app/devices/seam/connect-webview", {
        provider,
        deviceType: "thermostat",
        // Explicitly pass the owner's userId so the server stores the connection
        // under the owner's account even when initiated by a team member.
        // For direct owners activeOwnerUserId is undefined (not sent) and the
        // server auto-resolves correctly.
        ...(activeOwnerUserId != null ? { targetOwnerId: activeOwnerUserId } : {}),
      });
      return res.json() as Promise<{ connectWebviewId: string; url: string; provider: string }>;
    },
    onSuccess: (data) => {
      setConnectingProvider(data.provider);
      setConnectStep("waiting");
      setConnectError(null);

      const popup = window.open(
        data.url,
        "seam-connect",
        "popup,width=600,height=750,resizable=yes,scrollbars=yes",
      );
      popupRef.current = popup;
      if (!popup) {
        setConnectError("Popup blocked. Please allow popups for this site and try again.");
        return;
      }

      pollIntervalRef.current = window.setInterval(async () => {
        try {
          const res = await fetch(
            `/api/app/devices/seam/connect-webview/${encodeURIComponent(data.connectWebviewId)}`,
            { credentials: "include" },
          );
          if (!res.ok) return;
          const body = (await res.json()) as { loginSuccessful: boolean };
          if (body.loginSuccessful) {
            clearConnectTimers();
            if (popupRef.current && !popupRef.current.closed) {
              try { popupRef.current.close(); } catch {}
            }
            popupRef.current = null;
            queryClient.invalidateQueries({ queryKey: ["/api/app/devices/connections"] });
            setShowConnectDialog(false);
            resetConnectFlow();
            toast({ title: "Account connected successfully" });
          }
        } catch {
          // ignore transient network errors; keep polling
        }
      }, 2500);

      popupCheckRef.current = window.setInterval(() => {
        if (popupRef.current && popupRef.current.closed) {
          clearConnectTimers();
          popupRef.current = null;
          setConnectStep("select");
          setConnectingProvider(null);
        }
      }, 1000);
    },
    onError: (error: Error) => {
      setConnectError(parseErrorMessage(error, "Failed to start connect flow"));
      toast({ title: error.message || "Failed to connect account", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/app/devices/connections/${id}`, undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/connections"] });
      toast({ title: "Account disconnected" });
    },
    onError: () => {
      toast({ title: "Failed to disconnect account", variant: "destructive" });
    },
  });

  // Helper to extract a human-readable error message from a failed
  // apiRequest. apiRequest throws an Error whose message is the response
  // body when the body is JSON, so we attempt to parse JSON out of it to
  // surface the server's friendly "preset_device_conflict" message.
  function parseApiError(err: unknown, fallback: string): string {
    if (err instanceof Error) {
      try {
        const m = err.message.match(/^\d+:\s*(.+)$/s);
        const body = m ? m[1] : err.message;
        const json = JSON.parse(body);
        if (json && typeof json.message === "string") return json.message;
      } catch {}
      return err.message || fallback;
    }
    return fallback;
  }

  // D14 — apply a preset now. Results come back per device and are also
  // recorded on the preset row (shown under the preset name).
  const [applyResultsById, setApplyResultsById] = useState<Record<number, PresetApplyResponse>>({});
  const applyPresetMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/app/devices/thermostat-presets/${id}/apply`, {});
      return (await res.json()) as PresetApplyResponse;
    },
    onSuccess: (result) => {
      setApplyResultsById((prev) => ({ ...prev, [result.presetId]: result }));
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/thermostat-presets"] });
      const { text, tone } = describeApplyResult(result);
      toast({
        title: tone === "error" ? "Preset partly applied" : "Preset applied",
        description: text,
        variant: tone === "error" ? "destructive" : undefined,
      });
    },
    onError: (err: unknown) => {
      toast({ title: parseErrorMessage(err, "Failed to apply preset"), variant: "destructive" });
    },
  });

  const createPresetMutation = useMutation({
    mutationFn: async () => {
      // Derive propertyId from the first selected device. Presets without
      // any devices selected save with propertyId=null. Server enforces
      // one-thermostat-one-preset and will 409 on conflict.
      const firstDeviceId = presetForm.deviceIds[0];
      const firstDevice = firstDeviceId
        ? (thermostatDevicesQuery.data || []).find((d) => d.deviceId === firstDeviceId)
        : null;
      const derivedPropertyId = firstDevice?.propertyId ?? null;
      const res = await apiRequest("POST", "/api/app/devices/thermostat-presets", {
        name: presetForm.name,
        propertyId: derivedPropertyId,
        deviceIds: presetForm.deviceIds,
        summerHeatSetPoint: presetForm.summerHeatSetPoint,
        summerCoolSetPoint: presetForm.summerCoolSetPoint,
        winterHeatSetPoint: presetForm.winterHeatSetPoint,
        winterCoolSetPoint: presetForm.winterCoolSetPoint,
        fanMode: presetForm.fanMode,
        allowMemberControl: presetForm.allowMemberControl,
        summerStartMonth: presetForm.summerStartMonth,
        summerStartDay: presetForm.summerStartDay,
        summerEndMonth: presetForm.summerEndMonth,
        summerEndDay: presetForm.summerEndDay,
        winterStartMonth: presetForm.winterStartMonth,
        winterStartDay: presetForm.winterStartDay,
        winterEndMonth: presetForm.winterEndMonth,
        winterEndDay: presetForm.winterEndDay,
      });
      return (await res.json()) as ThermostatPreset;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/thermostat-presets"] });
      setShowPresetDialog(false);
      resetPresetForm();
      toast({ title: "Preset created" });
    },
    onError: (err: unknown) => {
      toast({ title: parseApiError(err, "Failed to create preset"), variant: "destructive" });
    },
  });

  const updatePresetMutation = useMutation({
    mutationFn: async () => {
      if (!editingPreset) return;
      const firstDeviceId = presetForm.deviceIds[0];
      const firstDevice = firstDeviceId
        ? (thermostatDevicesQuery.data || []).find((d) => d.deviceId === firstDeviceId)
        : null;
      const derivedPropertyId = firstDevice?.propertyId ?? null;
      const res = await apiRequest("PATCH", `/api/app/devices/thermostat-presets/${editingPreset.id}`, {
        name: presetForm.name,
        propertyId: derivedPropertyId,
        deviceIds: presetForm.deviceIds,
        summerHeatSetPoint: presetForm.summerHeatSetPoint,
        summerCoolSetPoint: presetForm.summerCoolSetPoint,
        winterHeatSetPoint: presetForm.winterHeatSetPoint,
        winterCoolSetPoint: presetForm.winterCoolSetPoint,
        fanMode: presetForm.fanMode,
        allowMemberControl: presetForm.allowMemberControl,
        summerStartMonth: presetForm.summerStartMonth,
        summerStartDay: presetForm.summerStartDay,
        summerEndMonth: presetForm.summerEndMonth,
        summerEndDay: presetForm.summerEndDay,
        winterStartMonth: presetForm.winterStartMonth,
        winterStartDay: presetForm.winterStartDay,
        winterEndMonth: presetForm.winterEndMonth,
        winterEndDay: presetForm.winterEndDay,
      });
      return (await res.json()) as ThermostatPreset;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/thermostat-presets"] });
      setShowPresetDialog(false);
      setEditingPreset(null);
      resetPresetForm();
      toast({ title: "Preset updated" });
    },
    onError: (err: unknown) => {
      toast({ title: parseApiError(err, "Failed to update preset"), variant: "destructive" });
    },
  });

  const duplicatePresetMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("POST", `/api/app/devices/thermostat-presets/${id}/duplicate`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/thermostat-presets"] });
      toast({ title: "Preset duplicated" });
    },
    onError: (err: unknown) => {
      toast({ title: parseApiError(err, "Failed to duplicate preset"), variant: "destructive" });
    },
  });

  const deletePresetMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/app/devices/thermostat-presets/${id}`, undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/thermostat-presets"] });
      toast({ title: "Preset deleted" });
    },
    onError: (err: unknown) => {
      toast({ title: parseApiError(err, "Failed to delete preset"), variant: "destructive" });
    },
  });

  function resetPresetForm() {
    setPresetForm({ name: "", propertyId: "", summerHeatSetPoint: 68, summerCoolSetPoint: 73, winterHeatSetPoint: 68, winterCoolSetPoint: 73, fanMode: "auto", deviceIds: [], allowMemberControl: true, summerStartMonth: 5, summerStartDay: 1, summerEndMonth: 9, summerEndDay: 30, winterStartMonth: 10, winterStartDay: 1, winterEndMonth: 4, winterEndDay: 30 });
    setPresetStep(1);
  }

  function openEditPreset(preset: ThermostatPreset) {
    const controls = preset as ThermostatPresetWithControls;
    setEditingPreset(preset);
    setPresetForm({
      name: preset.name,
      propertyId: preset.propertyId ? String(preset.propertyId) : "",
      summerHeatSetPoint: preset.summerHeatSetPoint,
      summerCoolSetPoint: preset.summerCoolSetPoint,
      winterHeatSetPoint: preset.winterHeatSetPoint,
      winterCoolSetPoint: preset.winterCoolSetPoint,
      fanMode: preset.fanMode,
      deviceIds: Array.isArray(preset.deviceIds) ? (preset.deviceIds as string[]) : [],
      allowMemberControl: controls.allowMemberControl ?? true,
      summerStartMonth: controls.summerStartMonth ?? 5,
      summerStartDay: controls.summerStartDay ?? 1,
      summerEndMonth: controls.summerEndMonth ?? 9,
      summerEndDay: controls.summerEndDay ?? 30,
      winterStartMonth: controls.winterStartMonth ?? 10,
      winterStartDay: controls.winterStartDay ?? 1,
      winterEndMonth: controls.winterEndMonth ?? 4,
      winterEndDay: controls.winterEndDay ?? 30,
    });
    setPresetStep(1);
    setShowPresetDialog(true);
  }

  const connections = connectionsQuery.data || [];
  const presets = presetsQuery.data || [];
  const properties = sortProperties(propertiesQuery.data || []);
  const filteredPresets = presets.filter((p) =>
    !presetSearch || String(p.name ?? "").toLowerCase().includes(presetSearch.toLowerCase())
  );

  const visibleDevices = filterThermostatRows(thermostatDevicesQuery.data || [], filteredPropertyId, statusFilter, attentionOnly)
    .sort(thermostatRowComparator(sortColumn, sortDirection, getPropertyName, formatProviderLabel));

  function toggleDeviceSort(column: typeof sortColumn) {
    if (sortColumn === column) setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    else { setSortColumn(column); setSortDirection("asc"); }
  }

  function getPropertyName(propId: number | null) {
    if (!propId) return "—";
    const prop = properties.find((p) => p.id === propId);
    return prop?.name || prop?.address || `Property #${propId}`;
  }

  return (
    <AppLayout>
      <div className="p-6">
        <ViewOnlyBanner area="devices" />
        <div className="mb-6 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-tour="thermostats-heading">Thermostats</h1>
              <p className="text-sm text-gray-500 mt-1">Manage your thermostat settings, presets, and connected accounts.</p>
            </div>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="thermostats" />
              <GuideButton pageKey="thermostats" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="thermostats" />
        </div>

        <div
          className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 mb-6"
          data-testid="callout-devices-tab-link"
        >
          <Info className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" aria-hidden="true" />
          <div className="text-sm">
            <p className="font-semibold text-blue-900">Looking for details on your devices?</p>
            <p className="text-blue-700 mt-0.5">
              Live readings, temperature charts, and per-device details are shown on the{" "}
              <strong>Devices</strong> tab inside each property.{" "}
              <a
                href="/app/properties"
                className="underline font-medium text-blue-800 hover:text-blue-600"
                data-testid="link-go-to-properties"
              >
                Go to My Properties
              </a>{" "}
              and open a property to view live device data.
            </p>
          </div>
        </div>

        <div className="border-b border-gray-200 mb-6">
          <div className="flex gap-0">
            {([
              { key: "connected_accounts" as ThermostatsTab, label: "Connected accounts" },
              { key: "presets" as ThermostatsTab, label: "Preset Settings" },
            ]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  effectiveTab === tab.key
                    ? "border-brand text-brand"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                data-testid={`tab-thermostats-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {effectiveTab === "connected_accounts" && (
          <div>
            <div className="mb-6">
              <h2 className="text-base font-semibold text-gray-900 mb-1">Connect a Device Account</h2>
              <p className="text-sm text-gray-500 mb-4">
                We currently support: Honeywell, Ecobee, Sensi, Google Nest, Carrier, Trane, Lennox, Daikin, tado°, Bryant, Bosch, Mysa, and SmartThings.
              </p>
              <Button disabled={readOnly}
                className="bg-brand hover:bg-brand-hover text-white"
                onClick={() => setShowConnectDialog(true)}
                data-testid="button-thermostat-connect"
              >
                Connect
              </Button>
            </div>

            <div className="border-b border-gray-200 mb-4">
              <div className="flex gap-0">
                {([
                  { key: "connected_accounts" as ConnectionSubTab, label: `Connected Accounts (${connections.length})` },
                  { key: "devices" as ConnectionSubTab, label: `Devices (${filteredPropertyId != null ? visibleDevices.length : (thermostatDevicesQuery.data?.length || 0)})` },
                ]).map((sub) => (
                  <button
                    key={sub.key}
                    onClick={() => setConnectionSubTab(sub.key)}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                      connectionSubTab === sub.key
                        ? "border-brand text-brand"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                    data-testid={`subtab-thermostats-${sub.key}`}
                  >
                    {sub.label}
                  </button>
                ))}
              </div>
            </div>

            {connectionSubTab === "connected_accounts" && (
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-tour="thermostats-list">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {connectionsQuery.isLoading ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">Loading...</td>
                      </tr>
                    ) : connections.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">No accounts connected yet. Click Connect to add one.</td>
                      </tr>
                    ) : connections.map((conn) => (
                      <tr key={conn.id} className="hover:bg-gray-50" data-testid={`row-thermostat-connection-${conn.id}`}>
                        <td className="px-4 py-3 text-gray-900 font-medium capitalize">
                          <span className="inline-flex items-center gap-2">
                            <Thermometer className="h-4 w-4 text-gray-400" />
                            {formatProviderLabel(conn.provider)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{conn.accountEmail || "—"}</td>
                        <td className="px-4 py-3">
                          <span className="text-green-600 text-sm">Connected</span>
                        </td>
                        <td className="px-4 py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1 hover:bg-gray-100 rounded" data-testid={`button-thermostat-actions-${conn.id}`}>
                                <MoreHorizontal className="h-4 w-4 text-gray-500" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem disabled={readOnly}
                                className="text-red-600"
                                onClick={() => deleteMutation.mutate(conn.id)}
                                data-testid={`button-thermostat-disconnect-${conn.id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Disconnect
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {connectionSubTab === "devices" && (
              <div>
              <div className="mb-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1 rounded-md border p-1" role="group" aria-label="Filter thermostats by status">
                  {(["all", "offline", "online"] as const).map((status) => (
                    <button
                      key={status}
                      type="button"
                      aria-pressed={statusFilter === status}
                      onClick={() => setStatusFilter(status)}
                      className={`rounded px-3 py-1.5 text-xs font-medium capitalize ${statusFilter === status ? "bg-brand text-white" : "text-gray-600 hover:bg-gray-100"}`}
                    >
                      {status === "all" ? "All" : status}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  aria-pressed={attentionOnly}
                  onClick={() => setAttentionDeepLink(!attentionOnly)}
                  className={`rounded-md border px-3 py-2 text-xs font-medium ${attentionOnly ? "border-amber-400 bg-amber-50 text-amber-900" : "text-gray-600 hover:bg-gray-50"}`}
                  data-testid="toggle-thermostat-attention"
                >
                  {attentionOnly ? "Needs attention · clear" : "Needs attention"}
                </button>
              </div>
              {filteredPropertyId != null && (
                <div className="mb-3 flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                  <span>Showing thermostats for: <strong>{getPropertyName(filteredPropertyId)}</strong></span>
                  <button
                    onClick={() => setFilteredPropertyId(null)}
                    className="ml-3 text-xs text-blue-500 hover:underline"
                    data-testid="button-clear-thermostat-property-filter"
                  >
                    Show all
                  </button>
                </div>
              )}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-tour="thermostats-health">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {(["name", "status", "provider", "account", "properties"] as const).map((column) => {
                        const label = column === "account" ? "Account" : column[0].toUpperCase() + column.slice(1);
                        return (
                          <th key={column} scope="col" aria-sort={sortColumn === column ? (sortDirection === "asc" ? "ascending" : "descending") : "none"} className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <button type="button" className="inline-flex items-center gap-1 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" onClick={() => toggleDeviceSort(column)} aria-label={`Sort by ${label}`}>
                              {label}{sortColumn === column ? <span aria-hidden="true">{sortDirection === "asc" ? "↑" : "↓"}</span> : null}
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {thermostatDevicesQuery.isLoading ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">Loading...</td>
                      </tr>
                    ) : thermostatDevicesQuery.isError ? (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-red-600">
                        Could not load thermostats. <button className="underline" onClick={() => thermostatDevicesQuery.refetch()}>Try again</button>
                      </td></tr>
                    ) : visibleDevices.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">
                          {filteredPropertyId != null
                            ? "No thermostats assigned to this property."
                            : "No devices found. Connect an account to see your thermostats here."}
                        </td>
                      </tr>
                    ) : visibleDevices.map((device) => {
                      const isEditing = editingDeviceId === device.deviceId;
                      const displayName = device.customName || device.seamName || "Unnamed device";
                      const isFlashing = flashingDeviceId === device.deviceId;
                      return (
                        <tr
                          key={device.deviceId}
                          ref={(el) => { deviceRowRefs.current[device.deviceId] = el; }}
                          className={`hover:bg-gray-50 transition-colors duration-700 ${isFlashing ? "bg-yellow-100 ring-2 ring-inset ring-yellow-400" : ""}`}
                          data-testid={`row-thermostat-device-${device.deviceId}`}
                          data-highlighted={isFlashing ? "true" : undefined}
                        >
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  value={editingDeviceName}
                                  onChange={(e) => setEditingDeviceName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      const trimmed = editingDeviceName.trim();
                                      renameDeviceMutation.mutate({
                                        deviceId: device.deviceId,
                                        customName: trimmed || null,
                                      });
                                    } else if (e.key === "Escape") {
                                      setEditingDeviceId(null);
                                      setEditingDeviceName("");
                                    }
                                  }}
                                  autoFocus
                                  className="h-8 w-48"
                                  data-testid={`input-rename-device-${device.deviceId}`}
                                />
                                <button
                                  onClick={() => {
                                    const trimmed = editingDeviceName.trim();
                                    renameDeviceMutation.mutate({
                                      deviceId: device.deviceId,
                                      customName: trimmed || null,
                                    });
                                  }}
                                  disabled={renameDeviceMutation.isPending}
                                  className="p-1.5 hover:bg-green-50 rounded text-green-600"
                                  data-testid={`button-save-rename-${device.deviceId}`}
                                >
                                  <Check className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => { setEditingDeviceId(null); setEditingDeviceName(""); }}
                                  className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
                                  data-testid={`button-cancel-rename-${device.deviceId}`}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 group">
                                <Thermometer className="h-4 w-4 text-gray-400" />
                                <div>
                                  <p className="font-medium text-gray-900" data-testid={`text-device-name-${device.deviceId}`}>
                                    {displayName}
                                  </p>
                                  {device.customName && device.seamName && device.customName !== device.seamName && (
                                    <p className="text-xs text-gray-400">From device: {device.seamName}</p>
                                  )}
                                </div>
                                <button
                                  onClick={() => {
                                    setEditingDeviceId(device.deviceId);
                                    setEditingDeviceName(device.customName || device.seamName || "");
                                  }}
                                  disabled={readOnly}
                                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-700 transition-opacity"
                                  data-testid={`button-edit-device-name-${device.deviceId}`}
                                >
                                  <Pencil className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-sm ${device.online === true ? "text-green-600" : device.online === false ? "text-gray-500" : "text-gray-400"}`}>
                              {device.online === true ? "Online" : device.online === false ? "Offline" : "Unknown"}
                            </span>
                            {(device.issueReasons ?? []).map((reason) => (
                              <span key={reason} className="ml-1.5 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                                {reason === "out_of_range" ? "Out of range" : reason === "unassigned" ? "Unassigned" : "Offline"}
                              </span>
                            ))}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{formatProviderLabel(device.provider)}</td>
                          <td className="px-4 py-3 text-gray-600">{device.accountEmail || "—"}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1.5">
                              <Select
                                value={device.propertyId ? String(device.propertyId) : "none"}
                                onValueChange={(v) => {
                                  assignDevicePropertyMutation.mutate({
                                    deviceId: device.deviceId,
                                    propertyId: v === "none" ? null : parseInt(v, 10),
                                  });
                                }}
                                disabled={readOnly}
                              >
                                <SelectTrigger className="h-8 w-[180px]" data-testid={`select-device-property-${device.deviceId}`}>
                                  <SelectValue placeholder="Primary property…" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">— Unassigned —</SelectItem>
                                  {properties.map((p) => (
                                    <SelectItem key={p.id} value={String(p.id)}>
                                      {p.name || p.address || `Property #${p.id}`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {device.propertyIds.length > 0 && (
                                <div className="flex flex-wrap items-center gap-1">
                                  {device.propertyIds.map((pid) => {
                                    const prop = properties.find((p) => p.id === pid);
                                    const label = prop?.name || prop?.address || `#${pid}`;
                                    return (
                                      <span
                                        key={pid}
                                        className="inline-flex items-center gap-1 text-[11px] bg-blue-50 text-blue-700 border border-blue-100 px-1.5 py-0.5 rounded"
                                        data-testid={`badge-device-property-${device.deviceId}-${pid}`}
                                      >
                                        {label}
                                        <button
                                          onClick={() => handleRemoveProperty(device.deviceId, pid)}
                                          disabled={readOnly || removePropertyMutation.isPending}
                                          className="text-blue-400 hover:text-red-500 disabled:opacity-40"
                                          data-testid={`button-remove-device-property-${device.deviceId}-${pid}`}
                                          title="Remove property"
                                        >
                                          <X className="h-2.5 w-2.5" />
                                        </button>
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                              {(device.propertyId == null ? properties : properties.filter((p) => !device.propertyIds.includes(p.id) && p.id !== device.propertyId)).length > 0 && (
                                <div className="flex items-center gap-1">
                                  <Select
                                    value="__add__"
                                    onValueChange={(v) => {
                                      if (v !== "__add__") handleAddProperty(device.deviceId, v);
                                    }}
                                    disabled={readOnly || addPropertyMutation.isPending}
                                  >
                                    <SelectTrigger className="h-7 w-[160px] text-xs" data-testid={`select-add-device-property-${device.deviceId}`}>
                                      <span className="flex items-center gap-1 text-gray-500">
                                        <Plus className="h-3 w-3" />
                                        Add property
                                      </span>
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(device.propertyId == null
                                        ? properties
                                        : properties.filter((p) => !device.propertyIds.includes(p.id) && p.id !== device.propertyId)
                                      ).map((p) => (
                                        <SelectItem key={p.id} value={String(p.id)} data-testid={`add-device-property-option-${p.id}`}>
                                          {p.name || p.address || `Property #${p.id}`}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </div>
            )}
          </div>
        )}

        {effectiveTab === "presets" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search..."
                  className="pl-9 w-[280px] h-9"
                  value={presetSearch}
                  onChange={(e) => setPresetSearch(e.target.value)}
                  data-testid="input-preset-search"
                />
              </div>
              <Button
                disabled={readOnly}
                className="bg-brand hover:bg-brand-hover text-white"
                onClick={() => { setEditingPreset(null); resetPresetForm(); setShowPresetDialog(true); }}
                data-testid="button-add-preset"
                data-tour="thermostats-presets"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Preset
              </Button>
            </div>

            <p className="text-xs text-gray-500 mb-3" data-testid="text-presets-apply-help">
              Save keeps your configuration. Apply now sends this preset's current-season set points and fan mode.
              Summer takes priority if date ranges overlap; no seasonal temperatures apply in date gaps.
              Member-portal restrictions apply when saved; physical restrictions require supported, verified devices.
              The thermostat's mode isn't changed, and thermostats that are off are skipped.
            </p>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Preset Name</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Summer Set Points</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Winter Set Points</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Fan</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Devices</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {presetsQuery.isLoading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">Loading...</td>
                    </tr>
                  ) : presetsQuery.isError ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-sm text-red-600">
                        Presets could not be loaded. <button className="underline" onClick={() => presetsQuery.refetch()}>Try again</button>
                      </td>
                    </tr>
                  ) : filteredPresets.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">No presets yet. Click "+ Add Preset" to create one.</td>
                    </tr>
                  ) : filteredPresets.map((preset) => (
                    <tr key={preset.id} className="hover:bg-gray-50" data-testid={`row-preset-${preset.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{preset.name}</div>
                        {Array.isArray(preset.controlPolicyResults) && preset.controlPolicyResults.map((r: { deviceId: string; status: string; message: string }) => (
                          <div key={r.deviceId} className="mt-1 text-[11px] text-gray-600">
                            {thermostatDevicesQuery.data?.find((d) => d.deviceId === r.deviceId)?.customName ||
                              thermostatDevicesQuery.data?.find((d) => d.deviceId === r.deviceId)?.seamName || r.deviceId}
                            {" · Physical control: "}{r.status} — {r.message}
                          </div>
                        ))}
                        {(() => {
                          const result = applyResultsById[preset.id] ?? readStoredApply(preset);
                          if (!result) {
                            const ids = Array.isArray(preset.deviceIds) ? (preset.deviceIds as string[]) : [];
                            return ids.length > 0 ? (
                              <div className="text-xs text-gray-400 mt-0.5" data-testid={`text-preset-apply-status-${preset.id}`}>Not applied yet</div>
                            ) : null;
                          }
                          const { text, tone } = describeApplyResult(result);
                          const problems = result.results.filter((r) => r.status === "failed" || r.status === "skipped" || r.status === "pending");
                          const policyResults = result.results.filter((r) => r.controlPolicy);
                          const nameOf = (id: string) => {
                            const d = (thermostatDevicesQuery.data || []).find((x) => x.deviceId === id);
                            return d?.customName || d?.seamName || id;
                          };
                          return (
                            <div
                              className={`text-xs mt-0.5 ${tone === "error" ? "text-red-600" : tone === "warn" ? "text-amber-600" : "text-gray-500"}`}
                              title={problems.map((r) => `${nameOf(r.deviceId)}: ${r.message ?? r.status}`).join("\n") || undefined}
                              data-testid={`text-preset-apply-status-${preset.id}`}
                            >
                              {text} · {new Date(result.appliedAt).toLocaleDateString()}
                              {policyResults.length > 0 && (
                                <ul className="mt-1 space-y-0.5 text-gray-500" data-testid={`text-preset-control-policy-${preset.id}`}>
                                  {policyResults.map((deviceResult) => (
                                    <li key={deviceResult.deviceId}>
                                      {nameOf(deviceResult.deviceId)} · Provider command policy (not verified here): {deviceResult.controlPolicy?.status} — {deviceResult.controlPolicy?.message}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 text-gray-600">
                          <span className="flex items-center gap-1">
                            <Flame className="h-3.5 w-3.5 text-orange-400" />
                            {preset.summerHeatSetPoint}°F
                          </span>
                          <span className="flex items-center gap-1">
                            <Snowflake className="h-3.5 w-3.5 text-blue-400" />
                            {preset.summerCoolSetPoint}°F
                          </span>
                        </div>
                        <p className="mt-1 text-[11px] text-gray-500">{presetSeasonRange(preset as ThermostatPresetWithControls, "summer")}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 text-gray-600">
                          <span className="flex items-center gap-1">
                            <Flame className="h-3.5 w-3.5 text-orange-400" />
                            {preset.winterHeatSetPoint}°F
                          </span>
                          <span className="flex items-center gap-1">
                            <Snowflake className="h-3.5 w-3.5 text-blue-400" />
                            {preset.winterCoolSetPoint}°F
                          </span>
                        </div>
                        <p className="mt-1 text-[11px] text-gray-500">{presetSeasonRange(preset as ThermostatPresetWithControls, "winter")}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-gray-600">
                          <Wind className="h-3.5 w-3.5 text-gray-400" />
                          {preset.fanMode}
                        </span>
                        <p className="mt-1 text-[11px] text-gray-500">
                          Member control: {(preset as ThermostatPresetWithControls).allowMemberControl ?? true ? "Allowed" : "Host-controlled"}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const ids = Array.isArray(preset.deviceIds) ? (preset.deviceIds as string[]) : [];
                          if (ids.length === 0) {
                            return <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">None</span>;
                          }
                          const deviceMap = new Map(
                            (thermostatDevicesQuery.data || []).map((d) => [d.deviceId, d.customName || d.seamName || "Unnamed device"]),
                          );
                          return (
                            <HoverCard openDelay={150} closeDelay={100}>
                              <HoverCardTrigger asChild>
                                <span
                                  className="text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 px-2 py-0.5 rounded cursor-default inline-flex items-center gap-1"
                                  data-testid={`badge-preset-devices-${preset.id}`}
                                >
                                  {ids.length} device{ids.length !== 1 ? "s" : ""}
                                </span>
                              </HoverCardTrigger>
                              <HoverCardContent
                                align="start"
                                className="w-64 p-2"
                                data-testid={`hover-preset-devices-${preset.id}`}
                              >
                                <p className="text-xs font-medium text-gray-700 mb-1.5">Linked thermostats</p>
                                <ul className="space-y-1">
                                  {ids.map((id) => (
                                    <li key={id} className="text-xs text-gray-600 flex items-center gap-1.5">
                                      <Thermometer className="h-3 w-3 text-gray-400 shrink-0" />
                                      <span className="truncate">{deviceMap.get(id) || id}</span>
                                    </li>
                                  ))}
                                </ul>
                              </HoverCardContent>
                            </HoverCard>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 hover:bg-gray-100 rounded" data-testid={`button-preset-actions-${preset.id}`}>
                              <MoreHorizontal className="h-4 w-4 text-gray-500" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditPreset(preset)} data-testid={`button-preset-edit-${preset.id}`}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={
                                readOnly ||
                                applyPresetMutation.isPending ||
                                !(Array.isArray(preset.deviceIds) && (preset.deviceIds as string[]).length > 0)
                              }
                              onClick={() => applyPresetMutation.mutate(preset.id)}
                              data-testid={`button-preset-apply-${preset.id}`}
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Apply now
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={readOnly || duplicatePresetMutation.isPending}
                              onClick={() => duplicatePresetMutation.mutate(preset.id)}
                              data-testid={`button-preset-duplicate-${preset.id}`}
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem disabled={readOnly}
                              className="text-red-600"
                              onClick={() => deletePresetMutation.mutate(preset.id)}
                              data-testid={`button-preset-delete-${preset.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>

      <Dialog
        open={showConnectDialog}
        onOpenChange={(open) => {
          if (!open && connectMutation.isPending) return;
          setShowConnectDialog(open);
          if (!open) resetConnectFlow();
        }}
      >
        <DialogContent
          className="flex flex-col max-h-[90vh]"
          onEscapeKeyDown={(e) => { if (connectMutation.isPending) e.preventDefault(); }}
          onInteractOutside={(e) => { if (connectMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (connectMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Connect a Thermostat Account</DialogTitle>
            <DialogDescription>
              Choose your thermostat brand. You'll sign in with that account in a popup window, and the device will be added automatically.
            </DialogDescription>
          </DialogHeader>

          {connectStep === "select" && (
            <div className="flex flex-col min-h-0 mt-2">
              <div className="overflow-y-auto flex-1 space-y-3 pr-1">
              {([
                { provider: "honeywell_resideo", label: "Honeywell" },
                { provider: "ecobee", label: "Ecobee" },
                { provider: "sensi", label: "Sensi" },
                { provider: "google_nest", label: "Google Nest" },
                { provider: "tado", label: "tado°" },
                { provider: "smartthings", label: "SmartThings" },
              ]).map((b) => (
                <button
                  key={b.provider}
                  onClick={() => connectMutation.mutate(b.provider)}
                  disabled={readOnly || connectMutation.isPending}
                  className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-brand hover:bg-brand-subtle transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`button-thermostat-provider-${b.provider}`}
                >
                  <span className="flex items-center gap-3">
                    {b.provider === "google_nest" ? (
                      <SiGoogle className="h-5 w-5 text-[#4285F4]" aria-hidden="true" />
                    ) : (
                      <Thermometer className="h-5 w-5 text-gray-400" />
                    )}
                    <span className="font-medium text-gray-900">{b.label}</span>
                  </span>
                  {connectMutation.isPending && connectingProvider === b.provider ? (
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  ) : (
                    <span className="text-xs text-gray-400">Connect →</span>
                  )}
                </button>
              ))}
              </div>
              {connectError && (
                <p className="text-sm text-red-600 mt-3" data-testid="text-thermostat-connect-error">
                  {connectError}
                </p>
              )}
              <div className="flex justify-end pt-2 flex-shrink-0">
                <Button
                  variant="outline"
                  onClick={() => setShowConnectDialog(false)}
                  data-testid="button-thermostat-connect-cancel"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {connectStep === "waiting" && (
            <div className="space-y-4 mt-2 text-center">
              <Loader2 className="h-10 w-10 animate-spin text-brand mx-auto" />
              <div>
                <p className="text-sm font-medium text-gray-900" data-testid="text-thermostat-connect-waiting">
                  Waiting for sign-in to complete…
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Finish signing in with {connectingProvider ? formatProviderLabel(connectingProvider) : "your provider"} in the popup. This dialog will close automatically when the device is added.
                </p>
              </div>
              {connectError && (
                <p className="text-sm text-red-600">{connectError}</p>
              )}
              <Button
                variant="outline"
                onClick={() => resetConnectFlow()}
                data-testid="button-thermostat-connect-cancel-waiting"
              >
                Cancel
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PresetDialog
        open={showPresetDialog}
        onOpenChange={(open) => { setShowPresetDialog(open); if (!open) { setEditingPreset(null); resetPresetForm(); } }}
        form={presetForm}
        setForm={setPresetForm}
        editing={!!editingPreset}
        editingPresetId={editingPreset?.id ?? null}
        properties={properties}
        devices={thermostatDevicesQuery.data || []}
        devicesLoading={thermostatDevicesQuery.isLoading}
        devicesError={thermostatDevicesQuery.isError}
        allPresets={presetsQuery.data || []}
        onSubmit={() => editingPreset ? updatePresetMutation.mutate() : createPresetMutation.mutate()}
        isPending={createPresetMutation.isPending || updatePresetMutation.isPending}
      />
    </AppLayout>
  );
}

export function PresetDialog({
  open,
  onOpenChange,
  form,
  setForm,
  editing,
  editingPresetId,
  properties,
  devices,
  devicesLoading,
  devicesError,
  allPresets,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: PresetFormState;
  setForm: (f: PresetFormState) => void;
  editing: boolean;
  editingPresetId: number | null;
  properties: Property[];
  devices: ThermostatDeviceRow[];
  devicesLoading: boolean;
  devicesError: boolean;
  allPresets: ThermostatPreset[];
  onSubmit: () => void;
  isPending: boolean;
}) {
  // Map every deviceId to the preset that already owns it, so the picker
  // can show a clear "Already in <Preset>" hint and disable the row. This
  // mirrors the server-side one-thermostat-one-preset rule and prevents
  // users from running into a 409 only after hitting Save.
  const otherPresetByDevice = new Map<string, ThermostatPreset>();
  for (const p of allPresets) {
    if (editingPresetId != null && p.id === editingPresetId) continue;
    const ids = Array.isArray(p.deviceIds) ? (p.deviceIds as string[]) : [];
    for (const id of ids) otherPresetByDevice.set(id, p);
  }

  // Group devices by their assigned PadPulse property so operators can
  // tick whole houses quickly. Unassigned devices fall into a final group.
  const propertyById = new Map(properties.map((p) => [p.id, p]));
  const groups = new Map<string, { label: string; devices: ThermostatDeviceRow[] }>();
  for (const d of devices) {
    const key = d.propertyId != null ? `p:${d.propertyId}` : "unassigned";
    const prop = d.propertyId != null ? propertyById.get(d.propertyId) : null;
    const label = prop ? (prop.name || prop.address || `Property #${prop.id}`) : "Unassigned";
    if (!groups.has(key)) groups.set(key, { label, devices: [] });
    groups.get(key)!.devices.push(d);
  }
  const orderedGroups = Array.from(groups.entries()).sort(([a], [b]) => {
    if (a === "unassigned") return 1;
    if (b === "unassigned") return -1;
    return groups.get(a)!.label.localeCompare(groups.get(b)!.label);
  });

  // Only trust "unavailable" once the live roster actually loaded; a failed
  // or in-flight read must not make every saved device look disconnected.
  const unavailableIds = devicesLoading || devicesError
    ? []
    : getUnavailablePresetDeviceIds(form.deviceIds, devices.map((d) => d.deviceId));

  function removeDevice(deviceId: string) {
    setForm({ ...form, deviceIds: form.deviceIds.filter((d: string) => d !== deviceId) });
  }

  function toggleDevice(deviceId: string) {
    setForm({
      ...form,
      deviceIds: form.deviceIds.includes(deviceId)
        ? form.deviceIds.filter((d: string) => d !== deviceId)
        : [...form.deviceIds, deviceId],
    });
  }

  function toggleGroup(groupDevices: ThermostatDeviceRow[]) {
    const eligible = groupDevices.filter((d) => !otherPresetByDevice.has(d.deviceId));
    const allSelected = eligible.length > 0 && eligible.every((d) => form.deviceIds.includes(d.deviceId));
    const ids = new Set(form.deviceIds);
    if (allSelected) {
      for (const d of eligible) ids.delete(d.deviceId);
    } else {
      for (const d of eligible) ids.add(d.deviceId);
    }
    setForm({ ...form, deviceIds: Array.from(ids) });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Preset" : "Add Preset"}</DialogTitle>
          <DialogDescription>
            Set seasonal heat and cool targets, then pick the thermostats this preset controls.
            A thermostat can only belong to one preset at a time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <Label htmlFor="preset-name">Preset name</Label>
            <Input
              id="preset-name"
              placeholder="e.g. Main House Default"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="input-preset-name"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sun className="h-4 w-4 text-amber-500" />
                <span className="text-sm font-semibold text-gray-900">Summer set points</span>
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label htmlFor="summer-heat" className="text-xs">Heat (°F)</Label>
                  <Input
                    id="summer-heat"
                    type="number"
                    min={50}
                    max={90}
                    value={form.summerHeatSetPoint}
                    onChange={(e) => setForm({ ...form, summerHeatSetPoint: Number(e.target.value) })}
                    data-testid="input-preset-summer-heat"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="summer-cool" className="text-xs">Cool (°F)</Label>
                  <Input
                    id="summer-cool"
                    type="number"
                    min={60}
                    max={100}
                    value={form.summerCoolSetPoint}
                    onChange={(e) => setForm({ ...form, summerCoolSetPoint: Number(e.target.value) })}
                    data-testid="input-preset-summer-cool"
                  />
                </div>
              </div>
              <ThermostatPresetSeasonFields
                season="summer"
                startMonth={form.summerStartMonth}
                startDay={form.summerStartDay}
                endMonth={form.summerEndMonth}
                endDay={form.summerEndDay}
                onChange={(field, value) => setForm({ ...form, [`summer${field[0].toUpperCase()}${field.slice(1)}`]: value })}
              />
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CloudSnow className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-semibold text-gray-900">Winter set points</span>
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label htmlFor="winter-heat" className="text-xs">Heat (°F)</Label>
                  <Input
                    id="winter-heat"
                    type="number"
                    min={50}
                    max={90}
                    value={form.winterHeatSetPoint}
                    onChange={(e) => setForm({ ...form, winterHeatSetPoint: Number(e.target.value) })}
                    data-testid="input-preset-winter-heat"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="winter-cool" className="text-xs">Cool (°F)</Label>
                  <Input
                    id="winter-cool"
                    type="number"
                    min={60}
                    max={100}
                    value={form.winterCoolSetPoint}
                    onChange={(e) => setForm({ ...form, winterCoolSetPoint: Number(e.target.value) })}
                    data-testid="input-preset-winter-cool"
                  />
                </div>
              </div>
              <ThermostatPresetSeasonFields
                season="winter"
                startMonth={form.winterStartMonth}
                startDay={form.winterStartDay}
                endMonth={form.winterEndMonth}
                endDay={form.winterEndDay}
                onChange={(field, value) => setForm({ ...form, [`winter${field[0].toUpperCase()}${field.slice(1)}`]: value })}
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
            <div>
              <Label htmlFor="preset-member-control" className="font-medium">Member temperature control</Label>
              <p className="mt-0.5 text-xs text-gray-500">Members can adjust heat and cool targets, but not HVAC mode.</p>
            </div>
            <Switch id="preset-member-control" checked={form.allowMemberControl} onCheckedChange={(allowMemberControl) => setForm({ ...form, allowMemberControl })} data-testid="switch-preset-member-control" />
          </div>

          <div>
            <Label>Fan mode</Label>
            <Select value={form.fanMode} onValueChange={(v) => setForm({ ...form, fanMode: v })}>
              <SelectTrigger data-testid="select-fan-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto</SelectItem>
                <SelectItem value="on">On</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Apply to thermostats</Label>
              <span className="text-xs text-gray-500" data-testid="text-selected-device-count">
                {form.deviceIds.length} selected
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Pick devices across one or more properties. Devices already in another preset are disabled.
            </p>
            <ThermostatPresetUnavailableDevices deviceIds={unavailableIds} onRemove={removeDevice} />
            {devicesLoading ? (
              <p className="text-sm text-gray-400">Loading devices…</p>
            ) : devicesError ? (
              <p className="text-sm text-red-600" data-testid="text-preset-devices-error">
                Couldn't load your thermostats. Saved selections are kept; try again before changing them.
              </p>
            ) : devices.length === 0 ? (
              <p className="text-sm text-gray-400">No thermostat devices found. Connect a thermostat account first.</p>
            ) : (
              <div className="space-y-3 max-h-[260px] overflow-y-auto border border-gray-200 rounded-lg p-2">
                {orderedGroups.map(([groupKey, group]) => {
                  const eligible = group.devices.filter((d) => !otherPresetByDevice.has(d.deviceId));
                  const allSelected = eligible.length > 0 && eligible.every((d) => form.deviceIds.includes(d.deviceId));
                  return (
                    <div key={groupKey} className="space-y-1">
                      <button
                        type="button"
                        className="w-full flex items-center justify-between px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded"
                        onClick={() => toggleGroup(group.devices)}
                        disabled={eligible.length === 0}
                        data-testid={`button-toggle-group-${groupKey}`}
                      >
                        <span className="flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5 text-gray-400" />
                          {group.label}
                          <span className="text-gray-400 font-normal">({group.devices.length})</span>
                        </span>
                        {eligible.length > 0 && (
                          <span className="text-[10px] uppercase tracking-wider text-brand">
                            {allSelected ? "Clear" : "Select all"}
                          </span>
                        )}
                      </button>
                      {group.devices.map((device) => {
                        const otherPreset = otherPresetByDevice.get(device.deviceId);
                        const disabled = !!otherPreset;
                        const displayName = device.customName || device.seamName || "Unnamed device";
                        const checked = form.deviceIds.includes(device.deviceId);
                        return (
                          <div
                            key={device.deviceId}
                            className={`flex items-center gap-3 p-2 rounded ml-1 ${
                              disabled
                                ? "opacity-60 cursor-not-allowed bg-gray-50"
                                : `cursor-pointer hover:bg-gray-50 ${
                                    checked ? "bg-brand-muted border border-brand/20" : "border border-transparent"
                                  }`
                            }`}
                            onClick={() => { if (!disabled) toggleDevice(device.deviceId); }}
                            data-testid={`device-option-${device.deviceId}`}
                          >
                            <Checkbox
                              checked={checked}
                              disabled={disabled}
                              onCheckedChange={() => { if (!disabled) toggleDevice(device.deviceId); }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {formatProviderLabel(device.provider)}
                                {device.accountEmail ? ` · ${device.accountEmail}` : ""}
                                {disabled && otherPreset ? ` · Already in "${otherPreset.name}"` : ""}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-preset-cancel">
              Cancel
            </Button>
            <Button
              className="bg-brand hover:bg-brand-hover text-white"
              onClick={onSubmit}
              disabled={isPending || !form.name.trim()}
              data-testid="button-preset-submit"
            >
              {isPending ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
